import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import {
  NavigationError,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { provideAppTransloco } from './core/i18n/transloco.providers';
import { routes } from './app.routes';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AppErrorHandler } from './core/error/app-error.handler';
import { isChunkLoadError, promptReloadForNewVersion } from './core/error/chunk-load';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { AuthService } from './core/services/auth.service';
import { ToastService } from './core/services/toast.service';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
      }),
      // A lazy-route chunk that 404s (stale deploy) otherwise fails silently — the user taps a nav
      // item and nothing happens. Surface the actionable reload prompt instead.
      withNavigationErrorHandler((e: NavigationError) => {
        if (isChunkLoadError(e.error)) {
          const transloco = inject(TranslocoService);
          promptReloadForNewVersion(inject(ToastService), (key) => transloco.translate(key));
        }
      }),
    ),
    provideHttpClient(withInterceptors([errorInterceptor, authInterceptor])),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    { provide: MAT_ICON_DEFAULT_OPTIONS, useValue: { fontSet: 'material-icons-outlined' } },
    provideAppTransloco(),
    provideAppInitializer(() => inject(AuthService).bootstrap()),
  ],
};
